import React from "react";
import {
    UploadIcon,
    ShieldCheckIcon,
    ArchiveIcon,
    QuestionMarkCircleIcon,
    CogIcon,
    TagIcon,
    DocumentReportIcon,
    CollectionIcon,
    FingerPrintIcon,
} from "@heroicons/react/outline";
import MenuItem from "../Menu/MenuItem";
import { canUseInterropol, interropolUrl } from "../../utils/interropol";

export default function AppSidebar({
                                       userLoggedIn,
                                       isOpen,
                                       closeSidebar,
                                       myAppsCount,
                                       inboxCount,
                                       myUsersCount,
                                       myArchiveCount,
                                       doLogOut,
                                   }) {
    if (!userLoggedIn) return null;

    return (
        <aside
            className={`md:flex md:w-72 md:flex-shrink-0 md:flex-col md:inset-y-0 ${
                isOpen
                    ? "w-screen h-screen bg-gray-100 -mx-6 px-2 overflow-y-auto"
                    : "max-w-xs hidden"
            }`}
            aria-label="Sidebar"
        >
            <div className="md:sticky md:top-6 overflow-y-auto rounded-2xl bg-gradient-to-b from-mobstr-navy to-mobstr-navy-dark p-3 shadow-md ring-1 ring-black/5">
                <ul className="space-y-0.5">
                    <MenuItem
                        displayName="Dashboard"
                        linkPath="dashboard"
                        closeSidebar={closeSidebar}
                    >
                        <svg
                            aria-hidden="true"
                            className="h-5 w-5 text-current"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z"></path>
                            <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z"></path>
                        </svg>
                    </MenuItem>

                    <MenuItem
                        displayName="Apps"
                        linkPath="apps"
                        hasNumber={myAppsCount}
                        closeSidebar={closeSidebar}
                    >
                        <svg
                            aria-hidden="true"
                            className="h-5 w-5 text-current"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"></path>
                        </svg>
                    </MenuItem>

                    {userLoggedIn?.OrganizationUser?.isAppAdmin && (
                        <MenuItem
                            displayName="Upload App"
                            linkPath="upload"
                            closeSidebar={closeSidebar}
                        >
                            <UploadIcon
                                className={
                                    "h-5 w-5 text-current"
                                }
                            />
                        </MenuItem>
                    )}

                    <MenuItem
                        displayName="Archive"
                        linkPath="archive"
                        hasNumber={myArchiveCount}
                        closeSidebar={closeSidebar}
                    >
                        <ArchiveIcon
                            className={
                                "h-5 w-5 text-current"
                            }
                        />
                    </MenuItem>

                    <MenuItem
                        displayName="Inbox"
                        linkPath="inbox"
                        hasNumber={inboxCount}
                        closeSidebar={closeSidebar}
                    >
                        <svg
                            aria-hidden="true"
                            className="h-5 w-5 text-current"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M8.707 7.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l2-2a1 1 0 00-1.414-1.414L11 7.586V3a1 1 0 10-2 0v4.586l-.293-.293z"></path>
                            <path
                                d="M3 5a2 2 0 012-2h1a1 1 0 010 2H5v7h2l1 2h4l1-2h2V5h-1a1 1 0 110-2h1a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V5z"></path>
                        </svg>
                    </MenuItem>

                    <MenuItem
                        displayName="Recent Vulnerabilities"
                        linkPath="vuln"
                        closeSidebar={closeSidebar}
                    >
                        <ShieldCheckIcon
                            className={
                                "h-5 w-5 text-current"
                            }
                        />
                    </MenuItem>

                    {userLoggedIn?.previewFeatures?.includes('compliance') && (
                        <MenuItem
                            displayName="Reporting"
                            linkPath="reporting"
                            closeSidebar={closeSidebar}
                        >
                            <DocumentReportIcon
                                className={
                                    "h-5 w-5 text-current"
                                }
                            />
                        </MenuItem>
                    )}
                    {userLoggedIn?.previewFeatures?.includes('teams') && (
                        <MenuItem
                            displayName="Teams Apps"
                            linkPath="teams-apps"
                            closeSidebar={closeSidebar}
                        >
                            <CollectionIcon
                                className={
                                    "h-5 w-5 text-current"
                                }
                            />
                        </MenuItem>
                    )}

                    {canUseInterropol(userLoggedIn) && (
                        <MenuItem
                            displayName="InterroPol"
                            href={interropolUrl("", { token: localStorage.getItem("tokenmob") })}
                            tag="Privacy"
                            closeSidebar={closeSidebar}
                        >
                            <FingerPrintIcon
                                className={
                                    "h-5 w-5 text-current"
                                }
                            />
                        </MenuItem>
                    )}

                    {userLoggedIn?.OrganizationUser?.isUserAdmin && (
                        <>
                            <MenuItem
                                displayName="Users"
                                linkPath="users"
                                hasNumber={myUsersCount}
                                closeSidebar={closeSidebar}
                            >
                                <svg
                                    aria-hidden="true"
                                    className="h-5 w-5 text-current"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                    ></path>
                                </svg>
                            </MenuItem>
                            <MenuItem
                                displayName="My Tags"
                                linkPath="mytags"
                                closeSidebar={closeSidebar}
                            >
                                <TagIcon
                                    className={
                                        "h-5 w-5 text-current"
                                    }
                                />
                            </MenuItem>
                        </>
                    )}

                    <MenuItem
                        displayName="FAQ"
                        linkPath="faq"
                        closeSidebar={closeSidebar}
                    >
                        <QuestionMarkCircleIcon
                            className={
                                "h-5 w-5 text-current"
                            }
                        />
                    </MenuItem>

                    {userLoggedIn?.OrganizationUser?.isSettingsAdmin && (
                        <MenuItem
                            displayName="Settings"
                            linkPath="settings"
                            closeSidebar={closeSidebar}
                        >
                            <CogIcon
                                className={
                                    "h-5 w-5 text-current"
                                }
                            />
                        </MenuItem>
                    )}

                    {userLoggedIn?.OrganizationUser?.isBillingAdmin && (
                        <MenuItem
                            displayName={"Billing"}
                            linkPath="billing"
                            closeSidebar={closeSidebar}
                        >
                            <svg
                                aria-hidden="true"
                                className="h-5 w-5 text-current"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M10 2a4 4 0 00-4 4v1H5a1 1 0 00-.994.89l-1 9A1 1 0 004 18h12a1 1 0 00.994-1.11l-1-9A1 1 0 0015 7h-1V6a4 4 0 00-4-4zm2 5V6a2 2 0 10-4 0v1h4zm-6 3a1 1 0 112 0 1 1 0 01-2 0zm7-1a1 1 0 100 2 1 1 0 000-2z"
                                ></path>
                            </svg>
                        </MenuItem>
                    )}

                    {(userLoggedIn?.isInternalUser || userLoggedIn?.isSuperUser) && (
                        <MenuItem
                            displayName="Admin Functions"
                            linkPath="usersreport"
                            closeSidebar={closeSidebar}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                version="1.1"
                                className="h-5 w-5 text-current"
                                fill="currentColor"
                                viewBox="-5.0 -10.0 110.0 135.0"
                            >
                                <path
                                    d="M84,2.5H30c-2.9,0-5.3,2.4-5.3,5.3v8.7H16c-2.9,0-5.3,2.4-5.3,5.3v70.5c0,2.9,2.4,5.3,5.3,5.3H70c2.9,0,5.3-2.4,5.3-5.3     v-8.7H84c2.9,0,5.3-2.4,5.3-5.3V7.8C89.2,4.9,86.9,2.5,84,2.5z M71,92.2c0,0.5-0.4,0.9-1,0.9H16c-0.5,0-0.9-0.4-0.9-0.9V21.7     c0-0.5,0.4-0.9,0.9-0.9h8.7v57.5c0,2.9,2.4,5.3,5.3,5.3h41V92.2z M84.9,78.3c0,0.5-0.4,1-1,1H30c-0.5,0-1-0.4-1-1V7.8     c0-0.5,0.4-1,1-1H84c0.5,0,1,0.4,1,1V78.3z"/>
                                <path
                                    d="M73,53.2H40.9c-1.2,0-2.2,1-2.2,2.2c0,1.2,1,2.2,2.2,2.2H73c1.2,0,2.2-1,2.2-2.2C75.2,54.2,74.2,53.2,73,53.2z"/>
                                <path
                                    d="M73,66.2H40.9c-1.2,0-2.2,1-2.2,2.2c0,1.2,1,2.2,2.2,2.2H73c1.2,0,2.2-1,2.2-2.2C75.2,67.2,74.2,66.2,73,66.2z"/>
                                <path
                                    d="M73,40.3H40.9c-1.2,0-2.2,1-2.2,2.2c0,1.2,1,2.2,2.2,2.2H73c1.2,0,2.2-1,2.2-2.2C75.2,41.2,74.2,40.3,73,40.3z"/>
                                <path
                                    d="M44.7,32h3.9c0.5,0,0.8-0.4,0.8-0.8v-6.2c0-0.5-0.4-0.8-0.8-0.8h-3.9c-0.5,0-0.8,0.4-0.8,0.8v6.2     C43.9,31.6,44.3,32,44.7,32z"/>
                                <path
                                    d="M54.4,32h3.9c0.5,0,0.8-0.4,0.8-0.8V20.5c0-0.5-0.4-0.8-0.8-0.8h-3.9c-0.5,0-0.8,0.4-0.8,0.8v10.6     C53.6,31.6,54,32,54.4,32z"/>
                                <path
                                    d="M64.1,32H68c0.5,0,0.8-0.4,0.8-0.8v-15c0-0.4-0.4-0.8-0.8-0.8h-3.9c-0.5,0-0.8,0.4-0.8,0.8v15C63.3,31.6,63.7,32,64.1,32     z"/>
                            </svg>
                        </MenuItem>
                    )}

                    <li className="mt-2 border-t border-white/10 pt-2">
                        <a
                            href="#"
                            className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-300 transition-colors hover:bg-white/5 hover:text-white"
                            onClick={doLogOut}
                        >
                            <svg
                                aria-hidden="true"
                                className="h-5 w-5 text-current"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z"
                                ></path>
                            </svg>
                            <span className="flex-1 whitespace-nowrap">
                Log Out
              </span>
                        </a>
                    </li>
                </ul>
            </div>
        </aside>
    );
}

